import React, { useState, useEffect } from 'react';
import PRODUCT_DB_MAP, { getDbStatus, resolveDbConfig } from '../config/productDatabaseMap';
import { API_BASE } from '../config/apiConfig';
import ConsolidatedGovernanceView from './ConsolidatedGovernanceView';

/**
 * ViewConfiguration — AI Layer Configuration Viewer + PDF Download
 *
 * 3 tabs:
 *   LLM-AI Config  — GenAI document (BI use cases, SQL patterns, tables) — Viewable + PDF
 *   Comm-AI Config  — Authorized agents, protocols, active grants — Viewable + PDF
 *   RAG-AI Status   — Just a status badge ("Active • Enforcing CEO Directives") — No details
 */
const ViewConfiguration = ({ clientConfig }) => {
  const [activeTab, setActiveTab] = useState('llm');
  const [llmConfig, setLlmConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetStatus, setResetStatus] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const clientId = clientConfig?.client_id || 'unknown';
  const companyName = clientConfig?.company_name || 'Client';
  const productType = clientConfig?.product_type || '';
  const resolvedDbConfig = clientConfig?.db_config || resolveDbConfig(null, productType);
  const dbStatus = getDbStatus(resolvedDbConfig, productType);

  const getToken = () => localStorage.getItem('pnp_access_token') || localStorage.getItem('pnp_id_token') || '';

  const handleFactoryReset = async () => {
    if (!resetConfirm) { setResetConfirm(true); return; }
    setResetting(true);
    setResetStatus(null);
    setResetConfirm(false);
    try {
      const resp = await fetch(`${API_BASE}/clients/${clientId}/policies/reset`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const result = await resp.json();
      if (resp.ok && result.reset) {
        setResetStatus({ success: true, message: result.message });
      } else {
        setResetStatus({ error: result.message || 'Reset failed — please try again' });
      }
    } catch {
      setResetStatus({ error: 'Reset failed — please try again' });
    }
    setResetting(false);
  };

  const handleDownloadPolicyPdf = async () => {
    setDownloadingPdf(true);
    try {
      const resp = await fetch(`${API_BASE}/clients/${clientId}/policies/consolidated/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(companyName || clientId).replace(/\s+/g, '-')}-Consolidated-Policy-Governance.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(`PDF download failed: ${e.message}`);
    }
    setDownloadingPdf(false);
  };

  useEffect(() => {
    if (activeTab === 'llm') fetchLlmConfig();
  }, [activeTab]);

  const fetchLlmConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/genai-preview`, { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        if (data && data.config_markdown) {
          setLlmConfig(data);
          setLoading(false);
          return;
        }
      }
    } catch { /* use local fallback */ }
    setLlmConfig(buildLocalLlmConfig());
    setLoading(false);
  };

  const buildLocalLlmConfig = () => {
    const db = resolvedDbConfig;
    const now = new Date().toISOString().slice(0, 19);
    return {
      config_markdown: [
        `# ${companyName} — LLM-AI Configuration`,
        `**Generated:** ${now}`,
        `**Product Type:** ${productType}`,
        `**Client ID:** ${clientId}`,
        `**Plan:** ${clientConfig?.plan || 'starter'}`,
        `**Tenant Type:** ${clientConfig?.tenant_type || 'INDEPENDENT'}`,
        '',
        '---',
        '',
        '## 1. Database Connection',
        db ? `| Field | Value |` : '| Field | Value |',
        db ? `|-------|-------|` : '|-------|-------|',
        db ? `| Engine | ${db.engine || 'PostgreSQL'} |` : '| Engine | Not configured |',
        db ? `| Host | ${db.host}:${db.port || 5432} |` : '| Host | — |',
        db ? `| Database | ${db.db} |` : '| Database | — |',
        db ? `| User | ${db.user || 'pnp_readonly'} (READ-ONLY) |` : '| User | — |',
        db ? `| Tables | ${db.tables || '—'} |` : '| Tables | — |',
        db ? `| Source | ${db.source || dbStatus.label} |` : '| Source | demo |',
        '',
        '## 2. SQL Intelligence Patterns',
        `The following pre-built SQL patterns are available for **${productType}**:`,
        '',
        ...((getSqlPatterns(productType)) || []),
        '',
        '## 3. BI Use Cases',
        ...((getBiUseCases(productType)) || []),
        '',
        '## 4. Security & Compliance',
        '- All queries are **read-only** (SELECT only)',
        '- PII is automatically redacted before LLM processing',
        '- Tenant isolation enforced via Row-Level Security (RLS)',
        '- Data sovereignty: only schema metadata sent to LLM, never actual row data',
        `- Database access: ${dbStatus.label === 'demo' ? 'Demo mode (hardcoded responses)' : `Live SQL via ${db?.engine || 'PostgreSQL'}`}`,
        '',
        '## 5. Uploaded AI Documents',
        '',
        '### 📄 RAG-AI Configuration',
        `- **File:** ${companyName}-RAG-AI.pdf`,
        `- **Purpose:** Retrieval-Augmented Generation — domain knowledge base for contextual Q&A`,
        `- **Status:** Configured`,
        `- **S3 Path:** clients/${clientId}/rag/RAG_AI.pdf`,
        '',
        '### 📄 GenAI Configuration',
        `- **File:** ${companyName}-GenAI.pdf`,
        `- **Purpose:** Generative AI — LLM prompt templates, SQL intelligence patterns, business rules`,
        `- **Status:** Configured`,
        `- **S3 Path:** clients/${clientId}/genai/GenAI.pdf`,
        '',
        '### 📄 Agentic-AI Configuration',
        `- **File:** ${companyName}-AgenticAI.pdf`,
        `- **Purpose:** Agent orchestration — MCP permissions, A2A protocols, external agent access`,
        `- **Status:** Configured`,
        `- **S3 Path:** clients/${clientId}/agentic/AgenticAI.pdf`,
        '',
        '## 6. Entity-Relationship Diagram',
        ...((() => { const er = getErDiagram(productType); if (!er) return ['(No ER diagram available for this product type)']; return [`Tables: ${er.tables.map(t => t.name).join(', ')}`, `Relationships: ${er.relationships.join(', ')}`]; })()),
      ].join('\n'),
    };
  };

  const handleDownloadPdf = (type) => {
    // Try backend PDF endpoint first
    const url = type === 'llm'
      ? `${API_BASE}/clients/${clientId}/download/genai`
      : `${API_BASE}/clients/${clientId}/download/agentic_ai`;

    fetch(url)
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Not available');
      })
      .then(data => {
        if (data.url) window.open(data.url, '_blank');
        else downloadAsText(type);
      })
      .catch(() => downloadAsText(type));
  };

  const downloadAsText = (type) => {
    const content = type === 'llm'
      ? (llmConfig?.config_markdown || buildLocalLlmConfig().config_markdown)
      : buildCommAiConfig();
    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${companyName.replace(/\s+/g, '-')}_${type === 'llm' ? 'LLM-AI' : 'Comm-AI'}_Config.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildCommAiConfig = () => {
    const now = new Date().toISOString().slice(0, 19);
    return [
      `# ${companyName} — Comm-AI Configuration`,
      `**Generated:** ${now}`,
      `**Client ID:** ${clientId}`,
      '',
      '---',
      '',
      '## 1. Authorized External Agents',
      '| Agent ID | Protocol | Scope | Status |',
      '|----------|----------|-------|--------|',
      '| pnp-agent-dist001 | MCP | inventory | Active |',
      '| pnp-agent-supplier | A2A | pricing | Active |',
      '| pnp-agent-signwell | A2A | agreements | Active |',
      '',
      '## 2. Active Permission Grants',
      '| Grantee | Access Level | Scope | Expiry |',
      '|---------|-------------|-------|--------|',
      `| pnp-agent-dist001 | READ_ONLY | inventory | ${new Date(Date.now() + 86400000).toISOString().slice(0, 10)} |`,
      '',
      '## 3. Protocol Configuration',
      '- **A2A (Agent-to-Agent):** Enabled — handles cross-tenant communication',
      '- **MCP (Model Context Protocol):** Enabled — handles data access requests',
      '- **SignWell Integration:** Enabled — digital agreement signing',
      '',
      '## 4. Communication Summary (This Month)',
      '| Metric | Value |',
      '|--------|-------|',
      '| A2A Messages Sent | 12 |',
      '| A2A Messages Received | 8 |',
      '| MCP Grants Issued | 3 |',
      '| MCP Grants Expired | 1 |',
      '| Agreements Sent | 2 |',
      '',
      '## 5. Data Sovereignty',
      '- Cross-tenant data sharing requires explicit permission grant',
      '- All grants are time-boxed (auto-expire)',
      '- Only non-private data is shared (is_private = FALSE)',
      '- Full audit trail maintained in permissions_map table',
    ].join('\n');
  };

  const tabStyle = (id) => ({
    padding: '10px 20px', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
    fontSize: '13px', fontWeight: 600,
    background: activeTab === id ? '#fff' : '#f0f0f0',
    color: activeTab === id ? '#1a1a2e' : '#666',
    borderBottom: activeTab === id ? '2px solid #1565C0' : '2px solid transparent',
  });

  const cardStyle = { background: '#fff', padding: '20px', borderRadius: '0 12px 12px 12px', border: '1px solid #e8e8e8' };

  // Download compliance rules as a formatted markdown file (client-side PDF-ready)
  const downloadCompliancePdf = (company, prodType) => {
    const now = new Date().toISOString().slice(0, 19);
    const content = [
      `# ${company} — Business Activity Compliance Enforcement`,
      `**Policy:** PNP-COMPLIANCE-001`,
      `**Generated:** ${now}`,
      `**Product Type:** ${prodType}`,
      `**Status:** Active — Enforcing`,
      '',
      '---',
      '',
      '## Policy Statement',
      'All product items and services published for this business must follow the rules and regulations',
      'of the registered country, state, and business license. A business MUST NOT publish, sell, or',
      'advertise products/services outside its legal scope.',
      '',
      '## Enforcement Layers',
      '1. **System Prompt Injection** — Every LLM call includes compliance enforcement prompt',
      '2. **RAG Knowledge Base** — Compliance rules embedded in RAG documents',
      '3. **Bedrock Guardrails** — Model-level blocking (topic denial + content filter)',
      '',
      '## Severity Model',
      '| Level | Meaning | Action |',
      '|-------|---------|--------|',
      '| 0 | Fully compliant | Allow |',
      '| 1 | Warning — edge category | Review recommended |',
      '| 2 | Mild violation | Flag for review |',
      '| 3 | Clear violation — outside scope | Block publishing |',
      '| 4 | High risk — legal exposure | Block + notify admin |',
      '| 5 | Critical — must block | Block + audit log + notify |',
      '',
      '## Violation Examples',
      '| Business Type | Violation | Severity | Action |',
      '|---------------|-----------|----------|--------|',
      '| Restaurant | Selling fireworks | 5 — Critical | BLOCK |',
      '| Restaurant | Selling prescription drugs | 5 — Critical | BLOCK |',
      '| Pharmacy | Selling firearms | 5 — Critical | BLOCK |',
      '| Grocery | Selling prescription meds | 4 — High risk | BLOCK + notify |',
      '| Gym/Fitness | Selling anabolic steroids | 5 — Critical | BLOCK |',
      '| Insurance | Offering banking services | 4 — High risk | BLOCK + notify |',
      '| Medical | Selling recreational drugs | 5 — Critical | BLOCK |',
      '| Any Business | Items outside license scope | 3 — Clear violation | FLAG + review |',
      '',
      '## Action on Violation',
      '1. Block publishing — item/service cannot be published to catalog',
      '2. Log compliance event — record to product_catalog_validation table',
      '3. Notify admin and subscriber — alert platform operator and business owner',
      '4. User message: "This item/service cannot be published as it falls outside the regulatory scope of your business license."',
      '5. Trigger Agentic-AI remediation — auto-suggest valid alternatives',
      '',
      '---',
      `_Policy: PNP-COMPLIANCE-001 | Platform: Plug-N-Play-Infra.ai | Generated: ${now}_`,
    ].join('\n');

    const blob = new Blob([content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${company.replace(/\s+/g, '-')}_Compliance_Rules.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '0' }}>
      <h3 style={{ margin: '0 0 16px' }}>⚙️ AI Layer Configuration</h3>
      <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
        View and download your AI configuration documents. These define how intelligence operates on your data.
      </p>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #e0e0e0' }}>
        <button onClick={() => setActiveTab('llm')} style={tabStyle('llm')}>⚙️ {(() => {
          const pt = (productType || '').toLowerCase();
          if (pt.includes('china-us') || pt.includes('strategic-intel')) return 'GeoPulse-LLM Config';
          if (pt.includes('procurement')) return 'ProcureGuard-LLM Config';
          if (pt.includes('bank') || pt.includes('financial')) return 'BankRisk-LLM Config';
          return 'LLM-AI Config';
        })()}</button>
        <button onClick={() => setActiveTab('comm')} style={tabStyle('comm')}>🐙 {(() => {
          const pt = (productType || '').toLowerCase();
          if (pt.includes('china-us') || pt.includes('strategic-intel')) return 'Executive Alerts Config';
          if (pt.includes('procurement')) return 'Workflow Config';
          if (pt.includes('bank') || pt.includes('financial')) return 'Risk Alerts Config';
          return 'Comm-AI Config';
        })()}</button>
        <button onClick={() => setActiveTab('rag')} style={tabStyle('rag')}>🛡️ {(() => {
          const pt = (productType || '').toLowerCase();
          if (pt.includes('china-us') || pt.includes('strategic-intel')) return 'CEO Directives & Governance';
          if (pt.includes('procurement')) return 'Policy & Compliance Rules';
          if (pt.includes('bank') || pt.includes('financial')) return 'Regulatory Compliance';
          return 'Boundary Guardrails';
        })()}</button>
      </div>

      {/* LLM-AI Tab */}
      {activeTab === 'llm' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h4 style={{ margin: 0 }}>LLM-AI Configuration — {companyName}</h4>
              <p style={{ color: '#666', fontSize: '12px', margin: '4px 0 0' }}>
                Defines BI use cases, SQL intelligence patterns, and database connection for this client.
              </p>
            </div>
            <button onClick={() => handleDownloadPdf('llm')} style={{
              padding: '8px 16px', background: '#1565C0', color: '#fff', border: 'none',
              borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              📄 Download PDF
            </button>
          </div>

          {loading ? <p>Loading configuration...</p> : (
            <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '20px', fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.8, whiteSpace: 'pre-wrap', maxHeight: '600px', overflow: 'auto', border: '1px solid #e0e0e0' }}>
              {llmConfig?.config_markdown || buildLocalLlmConfig().config_markdown}
            </div>
          )}

          {/* ── ER Intelligence Section ── */}
          {(() => { const erData = getErDiagram(productType); return erData ? (
            <div style={{ marginTop: '24px', background: '#fafbfc', borderRadius: '12px', border: '1px solid #e0e0e0', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <span style={{ fontSize: '20px' }}>🧠</span>
                <div>
                  <h4 style={{ margin: 0, color: '#1a1a2e' }}>ER Intelligence</h4>
                  <p style={{ margin: '2px 0 0', color: '#888', fontSize: '11px' }}>
                    Schema relationship map — used by the AI to understand table joins and data flow
                  </p>
                </div>
                <span style={{ marginLeft: 'auto', background: '#E8F5E9', color: '#2E7D32', padding: '3px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 600 }}>
                  ACTIVE
                </span>
              </div>

              {/* ER Diagram Visual */}
              <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e8e8e8', padding: '16px', overflow: 'auto' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', minHeight: '200px' }}>
                  {erData.tables.map((table, i) => (
                    <div key={i} style={{
                      background: table.color || '#E3F2FD', borderRadius: '8px', padding: '10px 14px',
                      border: `2px solid ${table.borderColor || '#90CAF9'}`, minWidth: '130px', textAlign: 'center',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    }}>
                      <div style={{ fontSize: '16px', marginBottom: '4px' }}>{table.icon}</div>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#1a1a2e' }}>{table.name}</div>
                      <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>{table.rows} rows</div>
                    </div>
                  ))}
                </div>

                {/* Relationship lines description */}
                <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '12px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 600, color: '#555', margin: '0 0 8px' }}>Key Relationships</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {erData.relationships.map((rel, i) => (
                      <span key={i} style={{
                        background: '#f5f5f5', padding: '4px 10px', borderRadius: '6px',
                        fontSize: '10px', color: '#444', fontFamily: 'monospace',
                      }}>
                        {rel}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <p style={{ fontSize: '10px', color: '#aaa', marginTop: '10px', textAlign: 'center' }}>
                The AI uses this relationship map to construct accurate multi-table queries without hallucination.
              </p>
            </div>
          ) : null; })()}
        </div>
      )}

      {/* Comm-AI Tab */}
      {activeTab === 'comm' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h4 style={{ margin: 0 }}>Comm-AI Configuration — {companyName}</h4>
              <p style={{ color: '#666', fontSize: '12px', margin: '4px 0 0' }}>
                Authorized agents, protocols, active grants, and communication summary.
              </p>
            </div>
            <button onClick={() => handleDownloadPdf('comm')} style={{
              padding: '8px 16px', background: '#1565C0', color: '#fff', border: 'none',
              borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              📄 Download PDF
            </button>
          </div>

          {/* Authorized Agents */}
          <div style={{ marginBottom: '16px' }}>
            <h5 style={{ margin: '0 0 8px', color: '#333' }}>Authorized External Agents</h5>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>Agent ID</th>
                  <th style={{ padding: '8px' }}>Protocol</th>
                  <th style={{ padding: '8px' }}>Scope</th>
                  <th style={{ padding: '8px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { id: 'pnp-agent-dist001', protocol: 'MCP', scope: 'inventory', status: 'Active' },
                  { id: 'pnp-agent-supplier', protocol: 'A2A', scope: 'pricing', status: 'Active' },
                  { id: 'pnp-agent-signwell', protocol: 'A2A', scope: 'agreements', status: 'Active' },
                ].map(agent => (
                  <tr key={agent.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>{agent.id}</td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ background: agent.protocol === 'MCP' ? '#E3F2FD' : '#F3E5F5', color: agent.protocol === 'MCP' ? '#1565C0' : '#7B1FA2', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>{agent.protocol}</span>
                    </td>
                    <td style={{ padding: '8px' }}>{agent.scope}</td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>{agent.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Protocol Status */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            {[
              { label: 'A2A Protocol', icon: '🔗', status: 'Enabled', desc: 'Agent-to-Agent communication' },
              { label: 'MCP Protocol', icon: '🔐', status: 'Enabled', desc: 'Model Context Protocol' },
              { label: 'SignWell', icon: '✍️', status: 'Enabled', desc: 'Digital agreements' },
            ].map(p => (
              <div key={p.label} style={{ background: '#f8f9fa', padding: '14px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e8e8e8' }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }}>{p.icon}</div>
                <div style={{ fontWeight: 600, fontSize: '12px' }}>{p.label}</div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>{p.desc}</div>
                <span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 600 }}>{p.status}</span>
              </div>
            ))}
          </div>

          {/* Communication Summary */}
          <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '16px', border: '1px solid #e8e8e8' }}>
            <h5 style={{ margin: '0 0 8px', color: '#333' }}>Communication Summary (This Month)</h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
              {[
                { label: 'A2A Sent', value: '12', color: '#1565C0' },
                { label: 'A2A Received', value: '8', color: '#7B1FA2' },
                { label: 'MCP Grants', value: '3', color: '#2E7D32' },
                { label: 'Grants Expired', value: '1', color: '#FF9800' },
                { label: 'Agreements', value: '2', color: '#E91E63' },
              ].map(m => (
                <div key={m.label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 700, color: m.color }}>{m.value}</div>
                  <div style={{ fontSize: '10px', color: '#888' }}>{m.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Boundary Guardrails Status Tab */}
      {activeTab === 'rag' && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h4 style={{ margin: 0 }}>🛡️ Boundary Guardrails — CEO Directives & Compliance</h4>
              <p style={{ color: '#666', fontSize: '12px', margin: '4px 0 0' }}>
                Business Activity Compliance Enforcement (PNP-COMPLIANCE-001)
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600 }}>
                ● Active — Enforcing
              </span>
              {/* Download consolidated governance PDF */}
              <button
                onClick={handleDownloadPolicyPdf}
                disabled={downloadingPdf}
                style={{
                  padding: '8px 16px', background: downloadingPdf ? '#90caf9' : '#1565C0', color: '#fff', border: 'none',
                  borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}
                title="Download consolidated governance PDF with all active policies, conflicts, and resolved directives"
              >
                {downloadingPdf ? '⏳ Downloading…' : '📄 Download Policy PDF'}
              </button>
              {/* Download compliance rules PDF */}
              <button onClick={() => downloadCompliancePdf(companyName, productType)} style={{
                padding: '8px 16px', background: '#546E7A', color: '#fff', border: 'none',
                borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                📋 Download Compliance PDF
              </button>
              {/* Factory Reset */}
              {!resetConfirm ? (
                <button
                  onClick={handleFactoryReset}
                  disabled={resetting}
                  style={{
                    padding: '8px 16px', background: '#fff', border: '1px solid #ef9a9a', color: '#c62828',
                    borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: resetting ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px',
                  }}
                  title="Deactivate all governance policies and return AI to product-type factory defaults"
                >
                  🔄 Reset to Factory
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#c62828', fontWeight: 600 }}>Confirm reset?</span>
                  <button onClick={handleFactoryReset} disabled={resetting}
                    style={{ background: '#c62828', color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                    {resetting ? '⏳' : 'Yes, Reset'}
                  </button>
                  <button onClick={() => setResetConfirm(false)}
                    style={{ background: '#eee', color: '#333', border: 'none', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '13px' }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Reset status feedback */}
          {resetStatus?.success && (
            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#2e7d32', marginBottom: '16px' }}>
              ✅ {resetStatus.message}
            </div>
          )}
          {resetStatus?.error && (
            <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', color: '#c62828', marginBottom: '16px' }}>
              ❌ {resetStatus.error}
            </div>
          )}

          {/* Compliance Policy Summary */}
          <div style={{ background: '#FFF3E0', borderRadius: '8px', padding: '16px', marginBottom: '16px', border: '1px solid #FFE0B2' }}>
            <div style={{ fontWeight: 700, color: '#E65100', marginBottom: '6px' }}>📋 Policy: PNP-COMPLIANCE-001</div>
            <p style={{ fontSize: '13px', color: '#333', margin: 0, lineHeight: 1.6 }}>
              All product items and services published for this business must follow the rules and regulations
              of the registered country, state, and business license. Items outside the legal scope of
              <strong> {productType || 'this business type'}</strong> will be <strong>blocked</strong> by RAG-AI.
            </p>
          </div>

          {/* 3 Enforcement Layers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'System Prompt', icon: '💬', status: 'Active', desc: 'Every LLM call includes compliance check' },
              { label: 'RAG Knowledge Base', icon: '📚', status: 'Active', desc: 'Compliance rules in RAG documents' },
              { label: 'Bedrock Guardrails', icon: '🛡️', status: 'Active', desc: 'Model-level blocking (REGULATED_GOODS)' },
            ].map(layer => (
              <div key={layer.label} style={{ background: '#f8f9fa', padding: '14px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e8e8e8' }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }}>{layer.icon}</div>
                <div style={{ fontWeight: 600, fontSize: '12px' }}>{layer.label}</div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>{layer.desc}</div>
                <span style={{ background: '#E8F5E9', color: '#2E7D32', padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 600 }}>{layer.status}</span>
              </div>
            ))}
          </div>

          {/* Violation Examples Table */}
          <div style={{ marginBottom: '20px' }}>
            <h5 style={{ margin: '0 0 8px', color: '#333' }}>Violation Examples</h5>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>Business Type</th>
                  <th style={{ padding: '8px' }}>Violation</th>
                  <th style={{ padding: '8px' }}>Severity</th>
                  <th style={{ padding: '8px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const pt = (productType || '').toLowerCase();
                  if (pt.includes('china-us') || pt.includes('strategic-intel')) {
                    return [
                      { biz: 'GeoPulse', violation: 'Providing classified/restricted government intelligence', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                      { biz: 'GeoPulse', violation: 'Recommending specific stock trades (not licensed)', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                      { biz: 'GeoPulse', violation: 'Generating content outside China-US bilateral scope', severity: '4 — High risk', action: 'BLOCK + notify', color: '#E65100' },
                      { biz: 'GeoPulse', violation: 'Disclosing subscriber portfolio details to other users', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                      { biz: 'GeoPulse', violation: 'Contradicting uploaded CEO Directives without flagging', severity: '3 — Clear violation', action: 'FLAG + review', color: '#FF9800' },
                      { biz: 'GeoPulse', violation: 'Presenting forecasts as certainties (no probability range)', severity: '3 — Clear violation', action: 'FLAG + review', color: '#FF9800' },
                      { biz: 'GeoPulse', violation: 'Queries about non-geopolitical topics (recipes, sports, etc.)', severity: '2 — Mild', action: 'REDIRECT', color: '#FF9800' },
                    ];
                  }
                  if (pt.includes('healthcare') || pt.includes('clinical')) {
                    return [
                      { biz: 'Healthcare', violation: 'Disclosing PHI without authorization (HIPAA)', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                      { biz: 'Healthcare', violation: 'Providing medical diagnoses or treatment advice', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                      { biz: 'Healthcare', violation: 'Prescribing medications or dosages', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                      { biz: 'Healthcare', violation: 'Sharing patient data across unauthorized departments', severity: '4 — High risk', action: 'BLOCK + notify', color: '#E65100' },
                      { biz: 'Healthcare', violation: 'Queries about non-clinical operations', severity: '2 — Mild', action: 'REDIRECT', color: '#FF9800' },
                    ];
                  }
                  if (pt.includes('bank') || pt.includes('financial')) {
                    return [
                      { biz: 'Bank', violation: 'Disclosing customer account details', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                      { biz: 'Bank', violation: 'Providing investment advice (not licensed)', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                      { biz: 'Bank', violation: 'Exposing risk model parameters externally', severity: '4 — High risk', action: 'BLOCK + notify', color: '#E65100' },
                      { biz: 'Bank', violation: 'Queries about non-banking operations', severity: '2 — Mild', action: 'REDIRECT', color: '#FF9800' },
                    ];
                  }
                  // Default: generic examples
                  return [
                    { biz: 'Restaurant', violation: 'Selling fireworks', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                    { biz: 'Restaurant', violation: 'Selling prescription drugs', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                    { biz: 'Grocery', violation: 'Selling prescription medications', severity: '4 — High risk', action: 'BLOCK + notify', color: '#E65100' },
                    { biz: 'Gym/Fitness', violation: 'Selling anabolic steroids', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                    { biz: 'Insurance', violation: 'Offering banking services', severity: '4 — High risk', action: 'BLOCK + notify', color: '#E65100' },
                    { biz: 'Medical', violation: 'Selling recreational drugs', severity: '5 — Critical', action: 'BLOCK', color: '#C62828' },
                    { biz: 'Any Business', violation: 'Items outside license scope', severity: '3 — Clear violation', action: 'FLAG + review', color: '#FF9800' },
                  ];
                })().map((v, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px', fontWeight: 500 }}>{v.biz}</td>
                    <td style={{ padding: '8px' }}>{v.violation}</td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ color: v.color, fontWeight: 600, fontSize: '11px' }}>{v.severity}</span>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ background: v.action === 'BLOCK' ? '#FFEBEE' : v.action.includes('notify') ? '#FFF3E0' : '#FFF8E1', color: v.color, padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>{v.action}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Severity Model */}
          <div style={{ marginBottom: '20px' }}>
            <h5 style={{ margin: '0 0 8px', color: '#333' }}>Severity Model</h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
              {[
                { level: '0', label: 'Compliant', color: '#4CAF50', bg: '#E8F5E9' },
                { level: '1', label: 'Warning', color: '#FF9800', bg: '#FFF3E0' },
                { level: '2', label: 'Mild', color: '#FF9800', bg: '#FFF3E0' },
                { level: '3', label: 'Clear', color: '#F44336', bg: '#FFEBEE' },
                { level: '4', label: 'High Risk', color: '#C62828', bg: '#FFEBEE' },
                { level: '5', label: 'Critical', color: '#B71C1C', bg: '#FFCDD2' },
              ].map(s => (
                <div key={s.level} style={{ background: s.bg, padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', fontWeight: 700, color: s.color }}>{s.level}</div>
                  <div style={{ fontSize: '10px', color: s.color, fontWeight: 600 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <p style={{ color: '#aaa', fontSize: '11px', textAlign: 'center' }}>
            Guardrails configuration is managed by the platform operator. Contact your administrator for changes.
            Full policy: config/business_compliance_policy.json
          </p>

          {/* CEO Policy Governance — consolidated view */}
          <ConsolidatedGovernanceView
            clientId={clientId}
            companyName={companyName}
            userRole={clientConfig?.user_role || 'viewer'}
          />
        </div>
      )}
    </div>
  );
};

// Helper: Get SQL patterns for a product type
function getSqlPatterns(productType) {
  const patterns = {
    'Restaurant.Type-1': [
      '| revenue_by_order_type | `SELECT order_type, SUM(total_amount) FROM orders GROUP BY order_type` | ⚡ Distilled-GenAI |',
      '| top_menu_items | `SELECT item_name, COUNT(*) FROM order_items GROUP BY item_name` | ⚡ Distilled-GenAI |',
      '| inventory_status | `SELECT item_name, quantity_on_hand FROM inventory_items WHERE qty < reorder` | ⚡ Distilled-GenAI |',
      '| daily_revenue | `SELECT DATE(order_date), SUM(total_amount) FROM orders GROUP BY 1` | ⚡ Distilled-GenAI |',
      '| staff_performance | `SELECT name, avg_rating FROM staff ORDER BY avg_rating DESC` | ⚡ Distilled-GenAI |',
    ],
    'Gym.Type-1': [
      '| member_count | `SELECT plan_name, COUNT(*) FROM memberships GROUP BY plan_name` | ⚡ Distilled-GenAI |',
      '| revenue_by_plan | `SELECT plan_name, SUM(amount) FROM payments GROUP BY plan_name` | ⚡ Distilled-GenAI |',
      '| class_attendance | `SELECT type_name, COUNT(*) FROM class_bookings GROUP BY type_name` | ⚡ Distilled-GenAI |',
      '| equipment_status | `SELECT equipment_type, status FROM equipment` | ⚡ Distilled-GenAI |',
      '| checkin_trend | `SELECT DATE(check_in_time), COUNT(*) FROM check_ins GROUP BY 1` | ⚡ Distilled-GenAI |',
    ],
    'Financial-Bank-Type1': [
      '| balance_sheet | `SELECT total_assets, total_liabilities, loan_to_deposit_ratio FROM balance_sheet_summary ORDER BY as_of_date DESC LIMIT 1` | ⚡ Distilled-GenAI |',
      '| asset_breakdown | `SELECT asset_type, SUM(balance) FROM assets GROUP BY asset_type` | ⚡ Distilled-GenAI |',
      '| deposit_stability | `SELECT stability_score, core_deposit_flag, COUNT(*) FROM deposit_behavior GROUP BY 1,2` | ⚡ Distilled-GenAI |',
      '| cashflow_projection | `SELECT flow_date, flow_type, SUM(amount) FROM cashflows WHERE scenario=base GROUP BY 1,2` | ⚡ Distilled-GenAI |',
      '| risk_exposure | `SELECT SUM(rwa), SUM(expected_loss), AVG(pd) FROM risk_metrics WHERE scenario=base` | ⚡ Distilled-GenAI |',
    ],
    'Grocery.Type-1': [
      '| daily_sales | `SELECT date, SUM(total_sales), SUM(total_orders) FROM sales_summary GROUP BY date` | ⚡ Distilled-GenAI |',
      '| top_products | `SELECT p.name, pp.total_sold, pp.revenue FROM product_performance pp JOIN products p USING(product_id) ORDER BY revenue DESC` | ⚡ Distilled-GenAI |',
      '| inventory_alert | `SELECT p.name, i.quantity, i.reorder_level FROM inventory i JOIN products p USING(product_id) WHERE i.quantity < i.reorder_level` | ⚡ Distilled-GenAI |',
      '| order_type_mix | `SELECT order_type, COUNT(*), SUM(total_amount) FROM orders GROUP BY order_type` | ⚡ Distilled-GenAI |',
      '| menu_popularity | `SELECT m.name, mp.total_orders, mp.revenue FROM menu_performance mp JOIN menu_items m USING(menu_item_id) ORDER BY total_orders DESC` | ⚡ Distilled-GenAI |',
    ],
    'Wellness-Intel': [
      '| active_members | `SELECT tier, COUNT(*) FROM members WHERE status=active GROUP BY tier` | ⚡ Distilled-GenAI |',
      '| revenue_by_source | `SELECT date, memberships, pt_packages, recovery, retail, total FROM revenue ORDER BY date DESC` | ⚡ Distilled-GenAI |',
      '| class_attendance | `SELECT c.name, c.enrolled, c.capacity FROM classes c ORDER BY enrolled DESC` | ⚡ Distilled-GenAI |',
      '| trainer_schedule | `SELECT t.name, t.rating, COUNT(s.session_id) FROM trainers t LEFT JOIN sessions s ON t.trainer_id=s.trainer_id GROUP BY t.trainer_id` | ⚡ Distilled-GenAI |',
      '| equipment_maintenance | `SELECT type, brand, last_maintenance, status FROM equipment WHERE status != active` | ⚡ Distilled-GenAI |',
      '| member_retention | `SELECT DATE_TRUNC(month, start_date), COUNT(*) FROM members GROUP BY 1 ORDER BY 1` | ⚡ Distilled-GenAI |',
    ],
    'China-US-Strategic-Intel.Type-1': [
      '| daily_pulse | `SELECT event_date, total_events, avg_severity, day_trajectory FROM view_china_us_daily_pulse ORDER BY event_date DESC LIMIT 7` | ⚡ Distilled-GenAI |',
      '| corporate_exposure | `SELECT company_ticker, company_name, events_affected, avg_impact_score, total_revenue_at_risk FROM view_china_us_corporate_exposure LIMIT 10` | ⚡ Distilled-GenAI |',
      '| severity_trend | `SELECT event_date, COUNT(*), AVG(overall_severity_score) FROM china_us_events WHERE event_date >= CURRENT_DATE - 14 GROUP BY 1` | ⚡ Distilled-GenAI |',
      '| forecast_scenarios | `SELECT scenario, predicted_severity_index, confidence_pct, narrative FROM china_us_forecasts WHERE approved_by IS NOT NULL` | ⚡ Distilled-GenAI |',
      '| sector_weights | `SELECT sector_name, weight, description FROM sector_weights WHERE is_active = TRUE ORDER BY weight DESC` | ⚡ Distilled-GenAI |',
      '| originator_comparison | `SELECT week_start, originator, event_count, critical_count FROM view_china_us_originator_comparison ORDER BY week_start DESC LIMIT 12` | ⚡ Distilled-GenAI |',
    ],
    'Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)': [
      '| tender_status | `SELECT status, COUNT(*), SUM(estimated_value) FROM tenders GROUP BY status` | ⚡ Distilled-GenAI |',
      '| vendor_concentration | `SELECT vendor_name, COUNT(*) as awards, SUM(contract_value) FROM contracts GROUP BY vendor_name ORDER BY awards DESC LIMIT 10` | ⚡ Distilled-GenAI |',
      '| single_bid_rate | `SELECT department, COUNT(*) FILTER (WHERE bid_count = 1)::float / COUNT(*) as single_bid_rate FROM tenders GROUP BY department` | ⚡ Distilled-GenAI |',
      '| budget_utilization | `SELECT department, allocated_budget, spent_amount, (spent_amount/allocated_budget)*100 as utilization_pct FROM budget_allocations` | ⚡ Distilled-GenAI |',
      '| risk_cases | `SELECT severity, status, COUNT(*) FROM ai_risk_cases GROUP BY severity, status` | ⚡ Distilled-GenAI |',
    ],
  };
  const ALIASES = {
    'Supermarket.Type-1': 'Grocery.Type-1',
    'Financial-Bank.Type-1': 'Financial-Bank-Type1',
  };
  const key = ALIASES[productType] || productType;
  const rows = patterns[key] || ['| (No distilled patterns) | — | 🤖 LLM only |'];
  return ['| Pattern | SQL Template | Source |', '|---------|-------------|--------|', ...rows];
}

// Helper: Get BI use cases for a product type
function getBiUseCases(productType) {
  const cases = {
    'Restaurant.Type-1': [
      '1. Revenue breakdown by order type (dine-in, takeaway, delivery, catering)',
      '2. Top-selling menu items by revenue and order count',
      '3. Inventory items below reorder point with shortage amounts',
      '4. Daily/weekly/monthly revenue trends',
      '5. Staff performance rankings by customer rating',
      '6. Reservation volume and table utilization',
      '7. Order channel analysis (mobile, website, walk-in, phone)',
    ],
    'Gym.Type-1': [
      '1. Active members by membership plan',
      '2. Revenue by membership plan',
      '3. Class attendance by type',
      '4. Equipment maintenance status',
      '5. Daily check-in trends',
      '6. Member retention analysis',
    ],
    'Financial-Bank-Type1': [
      '1. Balance sheet summary — total assets, liabilities, NIM, Tier 1 ratio',
      '2. Asset breakdown by type (loans, securities, cash, interbank)',
      '3. Deposit stability analysis — core vs non-core, decay rates',
      '4. Cashflow projections — base vs stress scenarios',
      '5. Risk-weighted assets and capital adequacy',
      '6. Loan prepayment behavior — CPR, SMM, PSA speed',
      '7. Loan-to-deposit ratio trend',
    ],
    'Grocery.Type-1': [
      '1. Daily/weekly sales across stores — Main vs Express',
      '2. Top-selling products by revenue and volume',
      '3. Inventory alerts — products below reorder level',
      '4. Order type mix — retail vs takeaway vs dine-in',
      '5. Most popular menu items by order count',
      '6. Kitchen order status — pending, preparing, ready, served',
      '7. Customer loyalty points distribution',
      '8. Supplier delivery status — pending, in-transit, delivered',
      '9. Payment method breakdown — UPI, card, cash, online',
      '10. Peak sales hours and store comparison',
    ],
    'Wellness-Intel': [
      '1. Active members by membership tier (Basic, Performance, Elite, Youth)',
      '2. Revenue breakdown — memberships, PT packages, recovery services, retail',
      '3. Class attendance and capacity utilization',
      '4. Trainer availability and session load',
      '5. Client fitness assessment improvements over 90 days',
      '6. Equipment maintenance status and overdue items',
      '7. Member retention rate and growth trend',
      '8. PT session completion rate',
      '9. Facility utilization by hour',
      '10. Revenue trend — monthly comparison',
    ],
    'China-US-Strategic-Intel.Type-1': [
      '1. Daily Pulse — today\'s events with severity scores and 4-pillar classification',
      '2. Corporate Exposure — top 10 companies by China revenue-at-risk',
      '3. Sector Impact — 11 GICS sector scores for recent events',
      '4. Forecast Scenarios — Bull/Base/Bear probabilities with confidence tiers',
      '5. Originator Analysis — US-initiated vs China-initiated event comparison',
      '6. Export Control Tracker — semiconductor and technology restriction events',
      '7. Tariff Countdown — Nov 2026 deadline scenarios and days remaining',
      '8. Severity Trend — 14-day escalation/de-escalation trajectory',
      '9. Summit Diplomacy — bilateral meeting outcomes and market signals',
      '10. Sector Weight Model — GICS sector weighting for impact calculation',
    ],
    'Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)': [
      '1. Tender lifecycle — open, evaluation, awarded, contracted by department',
      '2. Vendor concentration — top vendors by award count and value',
      '3. Single-bid rate — departments with highest single-bid acceptance',
      '4. Budget utilization — allocated vs spent by department and district',
      '5. Risk cases — AI-detected anomalies by severity and status',
      '6. MSME participation — vendor diversity metrics by category',
      '7. Post-award variations — contracts exceeding 15% variation threshold',
      '8. CM directive alignment — KPI scorecard by district',
      '9. Cycle-time analysis — publication to payment stage delays',
      '10. Compliance rate — policy violations by category and district',
    ],
  };
  const ALIASES = {
    'Supermarket.Type-1': 'Grocery.Type-1',
    'Financial-Bank.Type-1': 'Financial-Bank-Type1',
  };
  const key = ALIASES[productType] || productType;
  return cases[key] || ['1. General operational queries', '2. Revenue analysis', '3. Customer insights'];
}

// Helper: Get ER diagram data for a product type
function getErDiagram(productType) {
  const diagrams = {
    'Restaurant.Type-1': {
      tables: [
        { name: 'orders', icon: '🧾', rows: '1.2K', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'order_items', icon: '📋', rows: '3.8K', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'menu_items', icon: '🍔', rows: '45', color: '#FFF3E0', borderColor: '#FFB74D' },
        { name: 'inventory_items', icon: '📦', rows: '120', color: '#E8F5E9', borderColor: '#81C784' },
        { name: 'staff', icon: '👨‍🍳', rows: '18', color: '#F3E5F5', borderColor: '#CE93D8' },
        { name: 'reservations', icon: '📅', rows: '340', color: '#FFF8E1', borderColor: '#FFD54F' },
        { name: 'payments', icon: '💳', rows: '1.2K', color: '#E0F7FA', borderColor: '#80DEEA' },
        { name: 'customers', icon: '👥', rows: '280', color: '#FCE4EC', borderColor: '#F48FB1' },
      ],
      relationships: [
        'orders → order_items (1:M)',
        'orders → payments (1:1)',
        'menu_items → order_items (1:M)',
        'customers → orders (1:M)',
        'staff → orders (M:1)',
        'inventory → menu_items (M:M)',
      ],
    },
    'Gym.Type-1': {
      tables: [
        { name: 'members', icon: '🏋️', rows: '450', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'memberships', icon: '📇', rows: '450', color: '#E8F5E9', borderColor: '#81C784' },
        { name: 'classes', icon: '🧘', rows: '24', color: '#FFF3E0', borderColor: '#FFB74D' },
        { name: 'class_bookings', icon: '📋', rows: '2.1K', color: '#FFF3E0', borderColor: '#FFB74D' },
        { name: 'check_ins', icon: '✅', rows: '8.5K', color: '#F3E5F5', borderColor: '#CE93D8' },
        { name: 'payments', icon: '💳', rows: '1.8K', color: '#E0F7FA', borderColor: '#80DEEA' },
        { name: 'equipment', icon: '🏗️', rows: '85', color: '#FFF8E1', borderColor: '#FFD54F' },
        { name: 'staff', icon: '👤', rows: '12', color: '#FCE4EC', borderColor: '#F48FB1' },
      ],
      relationships: [
        'members → memberships (1:1)',
        'members → check_ins (1:M)',
        'members → class_bookings (1:M)',
        'classes → class_bookings (1:M)',
        'members → payments (1:M)',
        'staff → classes (1:M)',
      ],
    },
    'Financial-Bank-Type1': {
      tables: [
        { name: 'bank', icon: '🏦', rows: '1', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'accounts', icon: '📊', rows: '250', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'products', icon: '📦', rows: '18', color: '#E8F5E9', borderColor: '#81C784' },
        { name: 'assets', icon: '📈', rows: '120', color: '#FFF3E0', borderColor: '#FFB74D' },
        { name: 'liabilities', icon: '📉', rows: '130', color: '#FFF3E0', borderColor: '#FFB74D' },
        { name: 'cashflows', icon: '💰', rows: '2.4K', color: '#F3E5F5', borderColor: '#CE93D8' },
        { name: 'deposit_behavior', icon: '🔄', rows: '130', color: '#E0F7FA', borderColor: '#80DEEA' },
        { name: 'loan_prepayment', icon: '🏠', rows: '120', color: '#E0F7FA', borderColor: '#80DEEA' },
        { name: 'risk_metrics', icon: '⚠️', rows: '250', color: '#FCE4EC', borderColor: '#F48FB1' },
        { name: 'balance_sheet', icon: '📋', rows: '30', color: '#FFF8E1', borderColor: '#FFD54F' },
      ],
      relationships: [
        'bank → accounts (1:M)',
        'products → accounts (1:M)',
        'accounts → assets (1:1)',
        'accounts → liabilities (1:1)',
        'accounts → cashflows (1:M)',
        'accounts → deposit_behavior (1:1)',
        'accounts → loan_prepayment (1:1)',
        'accounts → risk_metrics (1:M)',
        'bank → balance_sheet (1:M)',
      ],
    },
    'Grocery.Type-1': {
      tables: [
        { name: 'stores', icon: '🏪', rows: '2', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'products', icon: '🛒', rows: '60', color: '#E8F5E9', borderColor: '#81C784' },
        { name: 'categories', icon: '📂', rows: '10', color: '#E8F5E9', borderColor: '#81C784' },
        { name: 'inventory', icon: '📦', rows: '120', color: '#FFF3E0', borderColor: '#FFB74D' },
        { name: 'menu_items', icon: '🍽️', rows: '25', color: '#FFF8E1', borderColor: '#FFD54F' },
        { name: 'orders', icon: '🧾', rows: '608', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'order_items', icon: '📋', rows: '1.4K', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'kitchen_orders', icon: '👨‍🍳', rows: '270', color: '#F3E5F5', borderColor: '#CE93D8' },
        { name: 'customers', icon: '👥', rows: '50', color: '#FCE4EC', borderColor: '#F48FB1' },
        { name: 'payments', icon: '💳', rows: '608', color: '#E0F7FA', borderColor: '#80DEEA' },
        { name: 'suppliers', icon: '🚚', rows: '12', color: '#FFF3E0', borderColor: '#FFB74D' },
        { name: 'sales_summary', icon: '📊', rows: '59', color: '#FFF8E1', borderColor: '#FFD54F' },
      ],
      relationships: [
        'stores → orders (1:M)',
        'stores → inventory (1:M)',
        'customers → orders (1:M)',
        'orders → order_items (1:M)',
        'orders → payments (1:1)',
        'orders → kitchen_orders (1:1)',
        'products → order_items (1:M)',
        'menu_items → order_items (1:M)',
        'categories → products (1:M)',
        'suppliers → purchase_orders (1:M)',
      ],
    },
    'Wellness-Intel': {
      tables: [
        { name: 'members', icon: '🏋️', rows: '320', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'sessions', icon: '📋', rows: '2.4K', color: '#E3F2FD', borderColor: '#90CAF9' },
        { name: 'trainers', icon: '💪', rows: '12', color: '#F3E5F5', borderColor: '#CE93D8' },
        { name: 'classes', icon: '🧘', rows: '28', color: '#FFF3E0', borderColor: '#FFB74D' },
        { name: 'assessments', icon: '📊', rows: '960', color: '#E8F5E9', borderColor: '#81C784' },
        { name: 'equipment', icon: '🏗️', rows: '65', color: '#FFF8E1', borderColor: '#FFD54F' },
        { name: 'revenue', icon: '💰', rows: '180', color: '#E0F7FA', borderColor: '#80DEEA' },
      ],
      relationships: [
        'members → sessions (1:M)',
        'trainers → sessions (1:M)',
        'trainers → classes (1:M)',
        'members → assessments (1:M)',
        'members → classes (M:M via enrollment)',
      ],
    },
  };
  const ALIASES = {
    'Supermarket.Type-1': 'Grocery.Type-1',
    'Financial-Bank.Type-1': 'Financial-Bank-Type1',
  };
  const key = ALIASES[productType] || productType;
  return diagrams[key] || null;
}

export default ViewConfiguration;
